import React from "react";
import styled from "styled-components";

const WRAPPER_STYLES = {
  activeWarn: {
    solid: "bg-warning border-warning",
    outline: "border-warning bg-light-warning",
  },
  active: {
    solid: "bg-info border-info",
    outline: "border-info bg-light-info bg-hover-light",
  },
  warn: {
    solid: "border-transparent bg-hover-light",
    outline: "border-light bg-hover-light",
  },
  default: {
    solid: "border-transparent bg-hover-light",
    outline: "border-light bg-hover-light",
  },
};

const ICON_STYLES = {
  activeWarn: "btn-warning",
  active: "btn-info",
  warn: "btn-light-warning text-warning",
  default: "btn-light-info",
};

const TEXT_STYLES = {
  activeWarn: { solid: "text-white", outline: "text-warning" },
  active: { solid: "text-white", outline: "text-info" },
  warn: { solid: "text-warning", outline: "text-warning" },
  default: { solid: "text-dark-75", outline: "text-dark-75" },
};

const getVariant = (active, warn) => {
  if (active && warn) return "activeWarn";
  if (active) return "active";
  if (warn) return "warn";
  return "default";
};

// `all: unset` (below) strips the native focus outline along with everything
// else. It's set via the inline `style` prop, which otherwise beats any
// stylesheet rule regardless of pseudo-class — hence `!important` here to
// win back a visible keyboard-focus indicator.
const ResetButton = styled.button`
  &:focus-visible {
    outline: 2px solid #8950fc !important;
    outline-offset: 2px !important;
  }
`;

const PillButton = ({
  active,
  className = "p-4",
  iconClassName,
  noIcon,
  solidActive,
  text,
  warn,
  warnText,
  disabled,
  style,
  ...rest
}) => {
  const variant = getVariant(active, warn);
  const mode = solidActive ? "solid" : "outline";

  const wrapperClass = WRAPPER_STYLES[variant][mode];
  const textClass = TEXT_STYLES[variant][mode];
  const iconClass = ICON_STYLES[variant];
  const checkIconClass =
    mode === "solid" ? "text-white" : warn ? "text-warning" : "text-info";

  return (
    <ResetButton
      type="button"
      disabled={disabled}
      {...rest}
      className={`${className} rounded-lg border d-flex align-items-center transition-all ${wrapperClass} ${
        active ? "shadow-sm" : ""
      } ${disabled ? "opacity-60" : ""}`}
      style={{
        // `all: unset` strips every native <button> UA default (appearance,
        // font, margin, outline, background, native hover/active/focus glow —
        // Safari in particular keeps painting these even with appearance:none
        // alone). Bootstrap's bg-*/border-*/p-* utilities are !important, so
        // they still win back over "unset" for the properties we do want.
        all: "unset",
        boxSizing: "border-box",
        display: "flex",
        WebkitTapHighlightColor: "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        ...style,
      }}
    >
      {!noIcon && iconClassName && (
        <div
          className={`btn btn-icon btn-sm mr-3 ${iconClass}`}
          style={{
            width: "36px",
            height: "36px",
            minWidth: "36px",
            // Purely decorative — Bootstrap's .btn class brings its own
            // independent :hover color transition, which fought with our
            // active/warn/default colors. It's not a separate click target
            // (the outer <button> owns the click), so opt it out of hover.
            pointerEvents: "none",
          }}
        >
          <i className={iconClassName}></i>
        </div>
      )}
      <div className={`font-weight-boldest flex-grow-1 ${textClass}`}>
        <div>{text}</div>
        {warn && <div className="font-size-xs opacity-70">{warnText}</div>}
      </div>
      {!noIcon && active && (
        <i className={`fas fa-check-circle ${checkIconClass} ml-3`}></i>
      )}
    </ResetButton>
  );
};

export default PillButton;
